import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import axios from 'axios';

const NAVY_DARK = '#111b33';

export default function Navbar() {
  const { count } = useCart();
  const { customer } = useAuth();
  const { settings, todayHours } = useSettings();
  const [menuOpen, setMenuOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/categories').then(r => setCategories(r.data)).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) { navigate(`/shop?search=${encodeURIComponent(search.trim())}`); setSearch(''); setMenuOpen(false); }
  };

  return (
    <>
      {/* Top bar */}
      <div style={{ background: NAVY_DARK, color: 'rgba(255,255,255,0.75)', fontSize: 12, padding: '6px 0' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span>📍 {settings.store_address}, {settings.store_city}, {settings.store_state} {settings.store_zip}</span>
          <span>📞 <a href={`tel:${(settings.store_phone || '').replace(/\D/g, '')}`} style={{ color: 'rgba(255,255,255,0.85)' }}>{settings.store_phone}</a> &nbsp;|&nbsp; Today: {todayHours}</span>
        </div>
      </div>

      {/* Main nav */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 1000, background: '#1a2744',
        boxShadow: scrolled ? '0 2px 16px rgba(0,0,0,0.3)' : 'none',
        transition: 'box-shadow 0.2s'
      }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', height: 64, gap: 24 }}>
          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ width: 38, height: 38, background: '#c9a227', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 18, color: '#1a2744' }}>PP</div>
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>Premier Pawn</div>
              <div style={{ color: '#c9a227', fontWeight: 600, fontSize: 11 }}>& Tech Repair</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, marginLeft: 16 }} className="desktop-nav">
            {/* Shop dropdown */}
            <div style={{ position: 'relative' }}
              onMouseEnter={() => setShopOpen(true)}
              onMouseLeave={() => setShopOpen(false)}>
              <Link to="/shop" style={{ color: '#fff', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, fontSize: 14, borderRadius: 6, transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Shop ▾
              </Link>
              {shopOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', padding: '8px 0', minWidth: 220, zIndex: 200 }}>
                  <div style={{ padding: '8px 16px 4px', fontSize: 11, fontWeight: 700, color: '#9a9a9a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Browse by Category</div>
                  {categories.map(cat => (
                    <Link key={cat.id} to={`/shop/${cat.slug}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', fontSize: 14, color: '#1a2744', fontWeight: 500 }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8f7f2'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span>{cat.icon}</span> {cat.name}
                    </Link>
                  ))}
                  <div style={{ borderTop: '1px solid #e8e8e8', margin: '8px 0' }} />
                  <Link to="/shop" style={{ display: 'block', padding: '10px 16px', fontSize: 14, color: '#c9a227', fontWeight: 700 }}>View All Inventory →</Link>
                </div>
              )}
            </div>

            {[['Sell or Pawn', '/sell'], ['Tech Repair', '/tech-repair'], ['About', '/about'], ['Contact', '/contact']].map(([label, href]) => (
              <Link key={href} to={href} style={{ color: 'rgba(255,255,255,0.85)', padding: '8px 14px', fontWeight: 600, fontSize: 14, borderRadius: 6, transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {label}
              </Link>
            ))}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} style={{ display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 8, overflow: 'hidden', width: 220, flexShrink: 0 }} className="desktop-nav">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search inventory..."
              style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', padding: '8px 12px', color: '#fff', fontSize: 13 }} />
            <button type="submit" style={{ padding: '8px 12px', background: '#c9a227', border: 'none', color: '#1a2744', fontWeight: 700, fontSize: 14 }}>🔍</button>
          </form>

          {/* Icons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {customer ? (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Link to="/account" style={{ color: 'rgba(255,255,255,0.85)', padding: '8px 12px', fontSize: 13, fontWeight: 600, borderRadius: 6 }}>
                  Hi, {customer.first_name}
                </Link>
              </div>
            ) : (
              <Link to="/login" style={{ color: 'rgba(255,255,255,0.75)', padding: '8px 12px', fontSize: 13, fontWeight: 600 }}>Sign In</Link>
            )}
            <Link to="/cart" style={{ position: 'relative', color: '#fff', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: 14 }}>
              🛒
              {count > 0 && (
                <span style={{ position: 'absolute', top: 2, right: 2, background: '#c9a227', color: '#1a2744', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{count}</span>
              )}
            </Link>
            {/* Mobile menu */}
            <button onClick={() => setMenuOpen(o => !o)} style={{ color: '#fff', background: 'none', border: 'none', fontSize: 22, padding: 8, display: 'none' }} className="mobile-menu-btn">☰</button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div style={{ background: '#111b33', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '16px 20px' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', marginBottom: 16, gap: 8 }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search inventory..."
                style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 14 }} />
              <button type="submit" style={{ padding: '10px 16px', background: '#c9a227', border: 'none', borderRadius: 8, color: '#1a2744', fontWeight: 700 }}>Go</button>
            </form>
            {[['🛍️ Shop All', '/shop'], ['🎯 Guns & Sporting', '/shop/guns-sporting'], ['📱 Electronics', '/shop/electronics'],
              ['💍 Jewelry', '/shop/jewelry'], ['🔧 Tools', '/shop/tools'], ['💰 Sell or Pawn', '/sell'],
              ['🔧 Tech Repair', '/tech-repair'], ['👥 About Us', '/about'], ['📞 Contact', '/contact'],
              customer ? ['👤 My Account', '/account'] : ['🔑 Sign In', '/login']].map(([label, href]) => (
              <Link key={href} to={href} onClick={() => setMenuOpen(false)}
                style={{ display: 'block', padding: '12px 4px', color: 'rgba(255,255,255,0.85)', fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.07)', fontSize: 14 }}>
                {label}
              </Link>
            ))}
          </div>
        )}
      </nav>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </>
  );
}
